import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ChipRemoveEvent } from "@progress/kendo-angular-buttons";
import {
  CheckableSettings,
  CheckedState,
  FilterState,
  TreeItemLookup,
  TreeViewFilterSettings
} from "@progress/kendo-angular-treeview";
import { UserService } from "src/app/admin/user/shared/services/user.service";

/**
 * LocationsTreeViewDropDownComponent - A sample drop down tree view to with checkbox to select locations
 * To create drop down menu, following data fields should be available in your custom data objects.
 *
 * rootLocations    : array of root locations Ids or [-1] to select all root locations
 * checkedIds       : Selected locations Ids as array or [] to select none
 */

export interface userCheckDto {
  userId: number,
  userName: string
}

@Component({
  selector: "users-drop-down-check-box-tree-view",
  styles: [
    `
          .k-checkbox {
            border-color: black;
          }

          .k-chip {
          color: #fff;
          margin: 2px;
          background-color: #005ea8;
          float: left;
          border-radius: 4px;
          }

          .tree-view-container {
            width: fit-content;
          }
        `,
  ],
  templateUrl: './users-drop-down-check-box-tree-view-component.html',
})
export class UsersDropDownCheckBoxTreeViewComponent implements OnInit {

  // currently checked Tree view unique id
  @Input() selectedUsers: number[] = [];

  // Search key word to filter out
  @Input() searchTerm: string = "";

  // Emit an event when a user checked/ unchecked
  @Output() userCheckedEvent = new EventEmitter<userCheckDto[]>();

  //Emit an event when dropdown has no matching records for the search term
  @Output() userFilterResultEvent = new EventEmitter<boolean>();

  // Tree view shown locations
  public requestUsersFlatList: { userId: number, fullName: string, root: null | any }[] = [];

  // Checking component
  public userKey = 'userId';

  public expandedKeys: any[] = ["0"];

  public allText: string = '';

  constructor(private userService: UserService, private translateService: TranslateService) { }

  public ngOnInit(): void {
    this.translateService.get('_General_All_Text_').subscribe(translatedString => {
      this.allText = translatedString;
    })

    this.requestUsersFlatList.push({ userId: 0, fullName: this.allText, root: null })
    
    this.userService.getUsersByHomeLocation().subscribe(res => {
      this.requestUsersFlatList = this.requestUsersFlatList
        .concat(
          res.map((u: { userId: any; fullName: any; }) => ({ userId: u.userId, fullName: u.fullName, root: 0 }))
        )
    });
  }

  // Handle on Check/ Uncheck event
  public handleChecking(event: TreeItemLookup): void {
    setTimeout(() => {
      if (event.item.index == '0' && this.selectedUsers[this.selectedUsers.length - 1] == 0) this.selectedUsers.splice(0);
      this.userCheckedEvent.emit(this.mapToUserCheckDto());
    }, 0);
  }

  private mapToUserCheckDto() {
    return this.requestUsersFlatList.filter(u => this.selectedUsers.includes(u.userId) && u.userId != 0).map(u => {
      return { userId: u.userId, userName: u.fullName } as userCheckDto;
    })
  }

  public filterSettings: TreeViewFilterSettings = {
    ignoreCase: true,
    operator: "contains",
  };

  // checking settings
  public userSectionCheckableSettings(): CheckableSettings {
    return {
      checkChildren: true,
      checkParents: true,
      enabled: true,
      mode: 'multiple',
      checkOnClick: false,
    };
  }

  public onRemove($event: ChipRemoveEvent): void {
    const item = this.requestUsersFlatList.find(c => c.fullName == $event.sender.label);
    if (!item) return;

    this.selectedUsers.splice(this.selectedUsers.indexOf(item.userId), 1);
    setTimeout(() => {
      this.userCheckedEvent.emit(this.mapToUserCheckDto());
    }, 0);
  }

  public getChipLabels() {
    return this.requestUsersFlatList.filter(c => c.userId != 0 && this.selectedUsers.includes(c.userId))
      .filter((v, i, s) => s.indexOf(v) === i);
  }

  public isChecked = (dataItem: any, index: string): CheckedState => {
    if (index == "0") {
      if (this.selectedUsers.length == 0) return "none";
      if (this.selectedUsers.length == this.requestUsersFlatList.length - 1) return "checked";
      if (this.selectedUsers.length <= this.requestUsersFlatList.length - 1) return "indeterminate";
    } else if (dataItem.userId && this.selectedUsers.includes(dataItem.userId)) return "checked";
    return "none";
  }

  //Filter state change handler
  public onFilterStateChange(event: FilterState): void {
    if (event.matchCount == 0) {
      this.userFilterResultEvent.emit(false);
    } else {
      this.userFilterResultEvent.emit(true);
    }
  }
}
